import React, { useState, useEffect, useRef } from 'react';

import { 
  DisablableOption,
} from "@models";
import { 
  Character,
  CharacterItem,
  CharacterContainer,
  Armor,
  Campaign,
  CampaignInventory,
  CharacterInventory,
} from "@dndModels";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import { makeStyles } from "@utilities/theme_utilities";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import CheckBox from "@components/input/CheckBox";
import ViewItem from "@dndComponents/display/ViewItem";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';
import FlexBox from '@components/layout/FlexBox';
import ExpandButton from '@components/input/buttons/ExpandButton';
import CharacterViewInventoryItem from './CharacterViewInventoryItem';

interface Props {
  obj: Character | Campaign;
  inventory: CharacterInventory | CampaignInventory;
  cont: CharacterContainer;
  filtered: CharacterItem[];
  disabled: boolean;
  onOpen: (open_item: CharacterItem) => void;
  reload_time: number;
  log_it?: boolean;
}

const char_util = DandDCharacterUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CharacterContainerItem({
  obj,
  inventory,
  cont,
  filtered,
  disabled,
  onOpen,
  reload_time,
  log_it
}: Props) {
  const [prev_reload_time, setPrevReloadTime] = useState(0);
  const [ping, setPing] = useState(false);
  const [is_expanded, setIsExpanded] = useState((inventory instanceof CampaignInventory && cont.true_id.includes("Storage")) || (inventory instanceof CharacterInventory && cont.true_id.includes("Equipment")));
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);
  
  const useStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '4px',
      },
    });
  });

  useEffect(() => {
    if (reload_time !== prev_reload_time) {
      setPrevReloadTime(reload_time);
    }
  }, [reload_time, prev_reload_time]);

  const { classes, cx } = useStyles();

  const renderMoveTo = () => {
    if (["Equipment","Storage","Campaign Storage"].includes(cont.true_id)) {
      return (
        <FlexBox xs_expand={0} style={{ width: "100px" }}>
        </FlexBox>
      );
    } else {
      const options: DisablableOption[] = [];
      
      if (inventory instanceof CampaignInventory) {
        if (obj instanceof Campaign) {
          // This is the Campaign Inventory as seen by the GM, 
          // so we want to add the ability to send to any Character in the Campaign (through Mail).
          options.push(...obj.characters.map(char => {
            const opt: DisablableOption = {
              id: `char_id:${char._id}`,
              text: char.use_name
            };
            return opt;
          }));
        } else if (obj.campaign) {
          // This is the Campaign Inventory, viewed from a Character Sheet.
          // That means we want to add the Take option, to move it into their own inventory.
          options.push({ id: "Take", text: "Take" });
        }
      } else {
        if (obj instanceof Character && obj.campaign) {
          // This is Character Inventory, viewed from a Character Sheet of a Character in a Campaign.
          // That means we want to add the Send to Campaign Inventory option.
          // We also want to add the ability to send to any other Character in the Campaign (through Mail).
          options.push({ id: "Campaign", text: "Send to Campaign Inventory" });
          options.push(...obj.campaign.characters.filter(o => o._id !== obj._id).map(char => {
            const opt: DisablableOption = {
              id: `char_id:${char._id}`,
              text: char.use_name
            };
            return opt;
          }));
        }
      }
      return (
        <FlexBox xs_expand={0} style={{ width: "100px" }}>
          { options.length > 0 &&
            <SelectStringBox 
              name="Move To"
              value={""}
              options={options}
              small
              onChange={ async (changed: string) => {
                debugger;
                await connect_util.moveItem(obj, inventory, cont, changed);
                setPing(!ping);
              }}
            />
          }
        </FlexBox>
      );
    }
  }

  const renderEquip = () => {
    return (
      <FlexBox xs_expand={0} style={{ width: "100px" }}>
        { obj instanceof Character && inventory instanceof CharacterInventory && !["Equipment","Storage","Campaign Storage"].includes(cont.true_id) &&
          <CheckBox 
            name=""
            value={cont.equipped} 
            onChange={(changed: boolean) => {
              const char_item = inventory.items.find(o => o.true_id === cont.true_id);
              if (char_item) {
                if (changed) {
                  connect_util.equipItem(obj, char_item);
                } else {
                  connect_util.unequipItem(obj, char_item);
                }
                setPing(!ping);
              }
            }} 
          />
        }
      </FlexBox>
    );
  }

  const renderWeight = () => {
    if (["Equipment","Storage","Campaign Storage"].includes(cont.true_id)) {
      return (
        <FlexBox xs_expand={0} className="small_label gray">
          Contents Weight:&nbsp;
          { cont.max_weight > 0 ? 
            <span style={{
              color: Math.round(cont.content_weight*100)/100 > cont.max_weight ? 'red' : 'inherit'
            }}>{ Math.round(cont.content_weight*100)/100 }/{ cont.max_weight } lbs&nbsp;</span>
          : 
            <span>{ Math.round(cont.content_weight*100)/100 } lbs&nbsp;</span>
          }
        </FlexBox>
      );
    } else {
      return (
        <FlexBox xs_expand={0}>
          <FlexBox direction="column">
            <FlexBox>Container Weight: { cont.weight } lbs
            </FlexBox>
            <FlexBox className="small_label gray">
              Contents Weight:&nbsp;
              { cont.max_weight > 0 ? 
                <span style={{
                  color: Math.round(cont.content_weight*100)/100 > cont.max_weight ? 'red' : 'inherit'
                }}>{ Math.round(cont.content_weight*100)/100 }/{ cont.max_weight } lbs&nbsp;</span>
              : 
                <span>{ Math.round(cont.content_weight*100)/100 } lbs&nbsp;</span>
              }</FlexBox>
          </FlexBox>
        </FlexBox>
      );
    }
  }
  
  if (log_it) {
    console.log(cont);
  }

  const renderName = () => {
    return (
      <FlexBox xs_expand={0} style={{ width: "100px" }} className="header padded clickable" onClick={() => onOpen(cont) }>
        <ViewItem item={cont} character={ obj instanceof Character ? obj : undefined } />
      </FlexBox>
    );
  }

  const renderExpanded = () => {
    if (is_expanded) {
      return (
        <FlexBox direction="column" className="blackBackground bordered">
          <FlexBox direction="row">
            <FlexBox className="header padded centered" xs_expand={0} style={{ width: "100px" }}>
              &nbsp;Active
            </FlexBox>
            <FlexBox className="header padded">
              Name
            </FlexBox>
            <FlexBox xs_expand={0} style={{ width: "200px" }}>
            </FlexBox>
            <FlexBox className="header padded centered" xs_expand={0} style={{ width: "100px" }}>
              Weight
            </FlexBox>
            <FlexBox className="header padded centered" xs_expand={0} style={{ width: "100px" }}>
              Qty
            </FlexBox>
            <FlexBox className="header padded centered" xs_expand={0} style={{ width: "100px" }}>
              Cost (GP)
            </FlexBox>
            <FlexBox className="header padded centered" xs_expand={0} style={{ width: "100px" }}>
              Worn Type
            </FlexBox>
          </FlexBox>
          { filtered.filter(o => 
            o.in_char_container_id === cont.true_id && 
            o.item_type !== "Container").sort((a,b) => {return a.name.localeCompare(b.name)}).map((item) => {
            // console.log(item);
            return (
              <FlexBox key={item.true_id}>
                <CharacterViewInventoryItem
                  obj={obj}
                  inventory={inventory}
                  item={item}
                  disabled={disabled}
                  reload_time={reload_time}
                  onOpen={onOpen}
                />
              </FlexBox>
            );
          })}
        </FlexBox>
      );
    } 
    return null;
  }

  const render = () => {
    return (
      <FlexBox direction="column" className={cx(classes.root, "header")}>
        <FlexBox direction="row">
          <FlexBox xs_expand={0}>
            <ExpandButton
              is_expanded={is_expanded}
              onClick={() => setIsExpanded(!is_expanded) }
            />
          </FlexBox>
          { renderName() }
          { renderMoveTo() }
          { renderEquip() }
          { renderWeight() }
        </FlexBox>
        { renderExpanded() }
      </FlexBox>
    );
  }

  return (
    <div className="padded">{ render() }</div>
  );
}
